import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import JSZip from 'jszip'
import { useAPI } from '../../Contexts/APIContext'
import { ButtonOutline } from '../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import { ModalWindow } from '../../Components/GeneralComponents/ModalWindow/ModalWindow'
import errorToast from '../../Components/Toasts/ErrorToast'
import {
  LLM_MANIFEST_PROMPT,
  MAX_WEBP_BYTES,
  deriveToken,
  toTitleCase,
  warnIfNotPortrait916,
} from './webpRequirements'
import classes from './index.module.css'

// Per-row lifecycle status through the sequential import run.
const STATUS_LABEL = {
  pending: 'Pending',
  uploading: 'Uploading…',
  finalizing: 'Processing…',
  created: 'Created',
  skipped: 'Skipped (no manifest entry)',
  error: 'Error',
}

export function ImportZipModal({ onClose, onDone }) {
  const {
    getUrlNewOverlayFilterMedia,
    uploadOverlayFilterMedia,
    finalizeOverlayFilterMedia,
    createOverlayFilter,
  } = useAPI()

  const [rows, setRows] = useState([]) // [{ key, name, entry, file, status, message }]
  const [isRunning, setIsRunning] = useState(false)
  const [isDone, setIsDone] = useState(false)
  const [summary, setSummary] = useState('')
  const [isPromptOpen, setIsPromptOpen] = useState(false)
  const [promptCopied, setPromptCopied] = useState(false)

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(LLM_MANIFEST_PROMPT)
      setPromptCopied(true)
      setTimeout(() => setPromptCopied(false), 2000)
    } catch (e) {
      errorToast('Could not copy to clipboard')
    }
  }

  // Escape closes the modal, but not mid-run.
  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'Escape' && !isRunning) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  })

  const updateRow = (key, patch) => {
    setRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)))
  }

  // Read the zip, cross-reference manifest.json entries against the .webp files,
  // and build the row list (matched entries, missing-file errors, orphan skips).
  const handleZipChange = async event => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setRows([])
    setSummary('')
    setIsDone(false)
    try {
      const zip = await JSZip.loadAsync(file)

      const manifestFile = zip.file(/(^|\/)manifest\.json$/i)[0]
      if (!manifestFile) {
        errorToast('Zip is missing manifest.json')
        return
      }
      let manifest
      try {
        manifest = JSON.parse(await manifestFile.async('string'))
      } catch (e) {
        errorToast('manifest.json is not valid JSON')
        return
      }
      const entries = Array.isArray(manifest?.filters) ? manifest.filters : null
      if (!entries) {
        errorToast('manifest.json must have a "filters" array')
        return
      }

      // Index the .webp entries in the zip by basename (manifest "file" values are
      // plain filenames, so match on the last path segment).
      const webpByName = {}
      zip.forEach((path, zipEntry) => {
        if (zipEntry.dir) return
        if (!/\.webp$/i.test(path)) return
        const base = path.split('/').pop()
        webpByName[base] = zipEntry
      })

      const claimed = new Set()
      const nextRows = entries.map((entry, index) => {
        // Title Case the name and derive the token now so a bad name is flagged
        // as an error row BEFORE any upload (item 3: fail fast, never dead-end
        // after finalize on a token the backend regex would reject).
        const finalName = toTitleCase((entry?.name || '').trim())
        const token = deriveToken(finalName)
        const fileName = entry?.file
        const zipEntry = fileName ? webpByName[fileName] : undefined
        let status = 'pending'
        let message = ''
        if (!finalName) {
          status = 'error'
          message = 'Manifest entry is missing a name'
        } else if (!token) {
          status = 'error'
          message = 'Name must contain at least one letter'
        } else if (!fileName) {
          status = 'error'
          message = 'Manifest entry is missing a file'
        } else if (!zipEntry) {
          status = 'error'
          message = `No "${fileName}" in the zip`
        } else {
          claimed.add(fileName)
        }
        return {
          key: `entry-${index}`,
          name: finalName || fileName || `entry ${index + 1}`,
          entry,
          finalName,
          token,
          zipEntry: status === 'pending' ? zipEntry : null,
          status,
          message,
        }
      })

      // .webp files present in the zip with no manifest entry -> skipped rows.
      for (const base of Object.keys(webpByName)) {
        if (claimed.has(base)) continue
        nextRows.push({
          key: `orphan-${base}`,
          name: base,
          entry: null,
          zipEntry: null,
          status: 'skipped',
          message: '',
        })
      }

      setRows(nextRows)
    } catch (e) {
      errorToast(`Could not read zip: ${e.message ?? ''}`)
    }
  }

  // Process one manifest row end to end, reusing the exact single-filter flow.
  const processRow = async row => {
    // Read the .webp bytes, then apply the same type/size pre-checks as a single
    // upload (type is inferred — zip entries carry no MIME).
    const blob = await row.zipEntry.async('blob')
    const webp = new File([blob], row.entry.file, { type: 'image/webp' })
    if (webp.size > MAX_WEBP_BYTES) {
      throw { message: 'File is larger than 10MB' }
    }

    updateRow(row.key, { status: 'uploading' })
    const { upload_id, upload_url } = await getUrlNewOverlayFilterMedia()
    await uploadOverlayFilterMedia(upload_url, webp)

    updateRow(row.key, { status: 'finalizing' })
    const media = await finalizeOverlayFilterMedia(upload_id)
    warnIfNotPortrait916(media.width, media.height)

    // row.finalName / row.token were validated at row-build time, before upload.
    await createOverlayFilter(
      row.token,
      row.finalName,
      {
        tags: Array.isArray(row.entry.tags) ? row.entry.tags : [],
        mood: Array.isArray(row.entry.mood) ? row.entry.mood : [],
      },
      {
        media_f_id: media.media_f_id,
        format: 'animated_webp',
        loop: row.entry.loop !== false,
      }
    )
  }

  // Run every actionable row sequentially. A failure on one row is caught and
  // recorded, never aborting the rest.
  const handleImport = async () => {
    setIsRunning(true)
    let created = 0
    let failed = 0
    const skipped = rows.filter(r => r.status === 'skipped').length
    for (const row of rows) {
      if (row.status !== 'pending') {
        if (row.status === 'error') failed += 1
        continue
      }
      try {
        await processRow(row)
        updateRow(row.key, { status: 'created', message: '' })
        created += 1
      } catch (e) {
        failed += 1
        updateRow(row.key, {
          status: 'error',
          message: `${e.status ?? ''} ${e.message ?? 'Failed'}`.trim(),
        })
      }
    }
    setIsRunning(false)
    setIsDone(true)
    setSummary(`${created} created, ${failed} failed, ${skipped} skipped`)
    await onDone()
  }

  const actionable = rows.filter(r => r.status === 'pending').length

  return (
    <ModalWindow open onClose={() => !isRunning && onClose()}>
      <div className={classes.importModal}>
        <h2 className={classes.createTitle}>Import Filters from Zip</h2>
        <p className={classes.createSubtitle}>
          Zip must contain a manifest.json (&#123;&quot;filters&quot;:[&#123;file,
          name, tags, mood, loop&#125;]&#125;) plus the referenced .webp files.
          Every filter is created as a draft.
        </p>

        <div className={classes.promptSection}>
          <button
            type="button"
            className={classes.promptToggle}
            aria-expanded={isPromptOpen}
            onClick={() => setIsPromptOpen(o => !o)}
          >
            <span>Generating this zip with an LLM?</span>
            <svg
              className={`${classes.promptChevron} ${
                isPromptOpen ? classes.promptChevronOpen : ''
              }`}
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 6L15 12L9 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {isPromptOpen && (
            <div className={classes.promptBody}>
              <textarea
                className={classes.promptTextarea}
                value={LLM_MANIFEST_PROMPT}
                readOnly
                rows={15}
              />
              <div className={classes.promptActions}>
                <ButtonOutline
                  text={promptCopied ? 'COPIED' : 'COPY PROMPT'}
                  color="pinkBlack"
                  size="small"
                  onClick={handleCopyPrompt}
                />
              </div>
            </div>
          )}
        </div>

        <label className={classes.uploadButton}>
          {rows.length ? 'CHOOSE A DIFFERENT ZIP' : 'CHOOSE ZIP'}
          <input
            type="file"
            accept=".zip,application/zip"
            onChange={handleZipChange}
            disabled={isRunning}
            style={{ display: 'none' }}
          />
        </label>

        {rows.length > 0 && (
          <ul className={classes.importList}>
            {rows.map(row => (
              <li key={row.key} className={classes.importRow}>
                <span className={classes.importRowName}>{row.name}</span>
                <span
                  className={`${classes.importRowStatus} ${
                    classes[`importStatus_${row.status}`] || ''
                  }`}
                >
                  {STATUS_LABEL[row.status]}
                  {row.message ? ` — ${row.message}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}

        {summary && <p className={classes.importSummary}>{summary}</p>}

        <div className={classes.createActions}>
          <ButtonOutline
            text={isRunning ? 'IMPORTING…' : isDone ? 'DONE' : 'IMPORT'}
            color="pink"
            disable={isRunning || (!isDone && actionable === 0)}
            onClick={isDone ? onClose : handleImport}
          />
        </div>
      </div>
    </ModalWindow>
  )
}

ImportZipModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
}
